import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BarChart3,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Trash2,
  Vote,
  X,
} from "lucide-react";
import { Navigate } from "react-router-dom";
import { z } from "zod";

import { CandidateLayout } from "@/components/candidate/CandidateLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useIsAdmin } from "@/hooks/useUserRole";
import { useNoIndex } from "@/hooks/useCanonicalTag";

interface PollOption {
  id?: string;
  option_text: string;
  order_index: number;
  votes_count?: number;
}

interface Poll {
  id: string;
  title: string;
  question: string;
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  allow_change_vote: boolean;
  show_results_before_vote: boolean;
  created_at: string;
  options?: PollOption[];
}

const formSchema = z.object({
  title: z.string().trim().min(2, "Título obrigatório").max(120),
  question: z.string().trim().min(3, "Pergunta obrigatória").max(280),
  starts_at: z.string().optional(),
  ends_at: z.string().optional(),
  allow_change_vote: z.boolean(),
  show_results_before_vote: z.boolean(),
  is_active: z.boolean(),
  options: z
    .array(z.object({ option_text: z.string().trim().min(1, "Opção vazia").max(120) }))
    .min(2, "Mínimo 2 opções")
    .max(6, "Máximo 6 opções"),
});

const toLocalInput = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60_000).toISOString().slice(0, 16);
};
const toIso = (local: string) => (local ? new Date(local).toISOString() : null);

function classify(p: Poll): "active" | "scheduled" | "ended" | "draft" {
  const now = Date.now();
  const startsOk = !p.starts_at || new Date(p.starts_at).getTime() <= now;
  const endsOk = !p.ends_at || new Date(p.ends_at).getTime() > now;
  if (!p.is_active) return "draft";
  if (p.ends_at && new Date(p.ends_at).getTime() <= now) return "ended";
  if (!startsOk) return "scheduled";
  if (startsOk && endsOk) return "active";
  return "draft";
}

function PollFormDialog({
  open,
  onOpenChange,
  initial,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  initial: Poll | null;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [question, setQuestion] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [allowChange, setAllowChange] = useState(false);
  const [showResultsBefore, setShowResultsBefore] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [options, setOptions] = useState<{ option_text: string; id?: string }[]>([
    { option_text: "" },
    { option_text: "" },
  ]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setTitle(initial.title);
      setQuestion(initial.question);
      setStartsAt(toLocalInput(initial.starts_at));
      setEndsAt(toLocalInput(initial.ends_at));
      setAllowChange(initial.allow_change_vote);
      setShowResultsBefore(initial.show_results_before_vote);
      setIsActive(initial.is_active);
      setOptions(
        (initial.options ?? []).length >= 2
          ? initial.options!.map((o) => ({ option_text: o.option_text, id: o.id }))
          : [{ option_text: "" }, { option_text: "" }]
      );
    } else {
      setTitle("");
      setQuestion("");
      setStartsAt("");
      setEndsAt("");
      setAllowChange(false);
      setShowResultsBefore(false);
      setIsActive(true);
      setOptions([{ option_text: "" }, { option_text: "" }]);
    }
  }, [open, initial]);

  const addOption = () => {
    if (options.length < 6) setOptions([...options, { option_text: "" }]);
  };
  const removeOption = (i: number) => {
    if (options.length > 2) setOptions(options.filter((_, idx) => idx !== i));
  };
  const setOptionText = (i: number, text: string) => {
    const copy = [...options];
    copy[i] = { ...copy[i], option_text: text };
    setOptions(copy);
  };

  const handleSubmit = async () => {
    const parsed = formSchema.safeParse({
      title,
      question,
      starts_at: startsAt,
      ends_at: endsAt,
      allow_change_vote: allowChange,
      show_results_before_vote: showResultsBefore,
      is_active: isActive,
      options: options.map((o) => ({ option_text: o.option_text })),
    });
    if (!parsed.success) {
      toast({
        title: "Verifique os campos",
        description: parsed.error.issues[0]?.message ?? "Dados inválidos",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const payload = {
        title: title.trim(),
        question: question.trim(),
        starts_at: toIso(startsAt),
        ends_at: toIso(endsAt),
        allow_change_vote: allowChange,
        show_results_before_vote: showResultsBefore,
        is_active: isActive,
        created_by: user?.id ?? null,
      };

      let pollId = initial?.id;
      if (initial) {
        const { error } = await (supabase as any)
          .from("platform_polls")
          .update(payload)
          .eq("id", initial.id);
        if (error) throw error;
      } else {
        const { data, error } = await (supabase as any)
          .from("platform_polls")
          .insert(payload)
          .select("id")
          .single();
        if (error) throw error;
        pollId = data.id;
      }

      // Sync options: delete existing and reinsert (simpler; preserves cascade)
      if (initial) {
        await (supabase as any).from("platform_poll_options").delete().eq("poll_id", pollId);
      }
      const optRows = options.map((o, idx) => ({
        poll_id: pollId,
        option_text: o.option_text.trim(),
        order_index: idx,
      }));
      const { error: optErr } = await (supabase as any)
        .from("platform_poll_options")
        .insert(optRows);
      if (optErr) throw optErr;

      toast({ title: initial ? "Enquete atualizada" : "Enquete criada" });
      queryClient.invalidateQueries({ queryKey: ["admin-polls"] });
      queryClient.invalidateQueries({ queryKey: ["platform-poll"] });
      onOpenChange(false);
    } catch (err: any) {
      toast({
        title: "Erro ao salvar",
        description: err?.message ?? "Tente novamente",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial ? "Editar enquete" : "Nova enquete"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Título (chamada curta)</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} />
          </div>
          <div className="space-y-2">
            <Label>Pergunta</Label>
            <Textarea value={question} onChange={(e) => setQuestion(e.target.value)} maxLength={280} rows={2} />
          </div>

          <div className="space-y-2">
            <Label>Opções (2 a 6)</Label>
            {options.map((o, i) => (
              <div key={i} className="flex gap-2">
                <Input
                  value={o.option_text}
                  onChange={(e) => setOptionText(i, e.target.value)}
                  placeholder={`Opção ${i + 1}`}
                  maxLength={120}
                />
                {options.length > 2 && (
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeOption(i)}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            {options.length < 6 && (
              <Button type="button" variant="outline" size="sm" onClick={addOption}>
                <Plus className="h-4 w-4 mr-1" /> Adicionar opção
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Início (opcional)</Label>
              <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Fim (opcional)</Label>
              <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
            </div>
          </div>

          <div className="space-y-3 rounded-md border p-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm">Permitir alterar voto</Label>
                <p className="text-xs text-muted-foreground">Usuário pode mudar de opção depois.</p>
              </div>
              <Switch checked={allowChange} onCheckedChange={setAllowChange} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm">Mostrar resultados antes de votar</Label>
                <p className="text-xs text-muted-foreground">Se desligado, usuário só vê resultados após votar.</p>
              </div>
              <Switch checked={showResultsBefore} onCheckedChange={setShowResultsBefore} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm">Ativa</Label>
                <p className="text-xs text-muted-foreground">Enquete fica visível para os usuários.</p>
              </div>
              <Switch checked={isActive} onCheckedChange={setIsActive} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? "Salvando..." : "Salvar enquete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PollResultsDialog({
  poll,
  open,
  onOpenChange,
}: {
  poll: Poll | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const total = (poll?.options ?? []).reduce((s, o) => s + (o.votes_count ?? 0), 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Resultados — {poll?.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">{poll?.question}</p>
          <p className="text-xs text-muted-foreground">
            Total: <strong>{total}</strong> voto{total === 1 ? "" : "s"}
          </p>
          <div className="space-y-2">
            {(poll?.options ?? [])
              .slice()
              .sort((a, b) => a.order_index - b.order_index)
              .map((o) => {
                const pct = total > 0 ? Math.round(((o.votes_count ?? 0) / total) * 100) : 0;
                return (
                  <div key={o.id} className="relative rounded-md border p-3 overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-primary/15"
                      style={{ width: `${pct}%` }}
                    />
                    <div className="relative flex items-center justify-between text-sm">
                      <span className="font-medium truncate">{o.option_text}</span>
                      <span className="text-xs text-muted-foreground">
                        {o.votes_count ?? 0} · {pct}%
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function PollsAdmin() {
  useNoIndex();
  const { isAdmin, isLoading: roleLoading } = useIsAdmin();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState<Poll | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [results, setResults] = useState<Poll | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Poll | null>(null);

  const { data: polls = [], isLoading } = useQuery({
    queryKey: ["admin-polls"],
    queryFn: async (): Promise<Poll[]> => {
      const { data, error } = await (supabase as any)
        .from("platform_polls")
        .select("*, options:platform_poll_options(id, option_text, order_index, votes_count)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Poll[];
    },
    enabled: !!isAdmin,
  });

  const toggleActive = useMutation({
    mutationFn: async (poll: Poll) => {
      const { error } = await (supabase as any)
        .from("platform_polls")
        .update({ is_active: !poll.is_active })
        .eq("id", poll.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-polls"] });
      queryClient.invalidateQueries({ queryKey: ["platform-poll"] });
    },
    onError: (err: any) =>
      toast({ title: "Erro", description: err?.message, variant: "destructive" }),
  });

  const deletePoll = useMutation({
    mutationFn: async (poll: Poll) => {
      const { error } = await (supabase as any).from("platform_polls").delete().eq("id", poll.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Enquete excluída" });
      queryClient.invalidateQueries({ queryKey: ["admin-polls"] });
      queryClient.invalidateQueries({ queryKey: ["platform-poll"] });
    },
    onError: (err: any) =>
      toast({ title: "Erro ao excluir", description: err?.message, variant: "destructive" }),
  });

  const buckets = useMemo(() => {
    const map = { active: [], scheduled: [], ended: [], draft: [] } as Record<string, Poll[]>;
    polls.forEach((p) => map[classify(p)].push(p));
    return map;
  }, [polls]);

  if (roleLoading) {
    return (
      <CandidateLayout maxWidth="6xl">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-64 w-full" />
      </CandidateLayout>
    );
  }
  if (!isAdmin) return <Navigate to="/candidate/dashboard" replace />;

  const renderList = (items: Poll[]) => {
    if (items.length === 0) {
      return <p className="text-sm text-muted-foreground py-6 text-center">Nenhuma enquete.</p>;
    }
    return (
      <div className="space-y-3">
        {items.map((p) => {
          const status = classify(p);
          const total = (p.options ?? []).reduce((s, o) => s + (o.votes_count ?? 0), 0);
          return (
            <Card key={p.id}>
              <CardContent className="p-4 flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-semibold text-sm">{p.title}</h3>
                    <Badge
                      variant={
                        status === "active" ? "default" : status === "ended" ? "secondary" : "outline"
                      }
                      className="text-[10px]"
                    >
                      {status === "active"
                        ? "Ativa"
                        : status === "scheduled"
                        ? "Agendada"
                        : status === "ended"
                        ? "Encerrada"
                        : "Rascunho"}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2">{p.question}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                    <span>{(p.options ?? []).length} opções</span>
                    <span>·</span>
                    <span>
                      {total} voto{total === 1 ? "" : "s"}
                    </span>
                    {p.starts_at && (
                      <>
                        <span>·</span>
                        <span>Início: {new Date(p.starts_at).toLocaleString("pt-BR")}</span>
                      </>
                    )}
                    {p.ends_at && (
                      <>
                        <span>·</span>
                        <span>Fim: {new Date(p.ends_at).toLocaleString("pt-BR")}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Resultados"
                    onClick={() => setResults(p)}
                  >
                    <BarChart3 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title={p.is_active ? "Desativar" : "Ativar"}
                    onClick={() => toggleActive.mutate(p)}
                  >
                    {p.is_active ? (
                      <PowerOff className="h-4 w-4" />
                    ) : (
                      <Power className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Editar"
                    onClick={() => {
                      setEditing(p);
                      setFormOpen(true);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Excluir"
                    onClick={() => setConfirmDelete(p)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <CandidateLayout maxWidth="6xl">
      <Helmet>
        <title>Enquetes - Admin Data Hire Club</title>
      </Helmet>

      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Vote className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Enquetes</h1>
            <p className="text-sm text-muted-foreground">
              Crie enquetes oficiais exibidas na página Comece Aqui.
            </p>
          </div>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-1" /> Nova enquete
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <Tabs defaultValue="active">
          <TabsList>
            <TabsTrigger value="active">Ativas ({buckets.active.length})</TabsTrigger>
            <TabsTrigger value="scheduled">Agendadas ({buckets.scheduled.length})</TabsTrigger>
            <TabsTrigger value="ended">Encerradas ({buckets.ended.length})</TabsTrigger>
            <TabsTrigger value="draft">Rascunho ({buckets.draft.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="active" className="mt-4">{renderList(buckets.active)}</TabsContent>
          <TabsContent value="scheduled" className="mt-4">{renderList(buckets.scheduled)}</TabsContent>
          <TabsContent value="ended" className="mt-4">{renderList(buckets.ended)}</TabsContent>
          <TabsContent value="draft" className="mt-4">{renderList(buckets.draft)}</TabsContent>
        </Tabs>
      )}

      <PollFormDialog open={formOpen} onOpenChange={setFormOpen} initial={editing} />
      <PollResultsDialog poll={results} open={!!results} onOpenChange={(v) => !v && setResults(null)} />

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir enquete?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Todos os votos serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDelete) deletePoll.mutate(confirmDelete);
                setConfirmDelete(null);
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CandidateLayout>
  );
}
